import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import React from 'react';
import {
	isAssignedPlannerOrAdmin,
	toSimpleLink,
} from '../../../lib/helpers/index';
import { linkCustomer, LinkCustomerLinkType } from '../helpers';
import {
	EventCategory,
	ProposalAcceptedByPlannerEventPropsEventFragment,
} from '#types';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import rpcShared from '@rockpapercoin/rpc-shared';
import { FlattenedUser } from '../../../types/user';
import { CalendarSourceType } from '../../CalendarLinks';
import { DefaultEventProps } from '../contract/helpers';

export const proposalAcceptedByPlannerEventPropsEventFragment = gql`
	fragment proposalAcceptedByPlannerEventPropsEvent on Event {
		id
		category
		vendor {
			id
			name
		}
		proposal {
			id
			title
			eventDate
			contact {
				firstName
				lastName
				customer {
					email
				}
				vendor {
					name
				}
				...getViewingUserDocumentRelationshipContact
			}
		}
	}
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
`;

export const getProposalAcceptedEventProps = (
	defaultEventProps: DefaultEventProps & {
		event: ProposalAcceptedByPlannerEventPropsEventFragment;
	},
	user: FlattenedUser
): DefaultEventProps => {
	// if a file isn't a component and it uses a hook then its name should start with `use`
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const router = useBlockingRouter();
	const updatedEventProps = { ...defaultEventProps };
	const proposalLink = {
		text: updatedEventProps.event.proposal?.title,
		link: {
			as: `/proposal/${ updatedEventProps.event.proposal?.id }`,
			href: '/proposal/[id]',
		},
	};
	const customerLink = updatedEventProps.event.proposal
		? linkCustomer(
			{ contact: updatedEventProps.event.proposal.contact },
			LinkCustomerLinkType.Contact
		  )
		: undefined;
	const vendorLink = {
		text: updatedEventProps.event.proposal?.contact?.vendor.name,
		link: {
			as: `/org/${ updatedEventProps.event.proposal?.contact?.vendor.id }`,
			href: '/org/[id]',
		},
	};
	const assignedPlannerOrAdmin = updatedEventProps.event.proposal?.contact
		?.customer.clientUser
		? isAssignedPlannerOrAdmin(
			user,
			updatedEventProps.event.proposal.contact.customer.clientUser
		  )
		: false;
	const isVendorDetail = router.pathname === '/org/[id]';

	updatedEventProps.icon = 'proposal';
	updatedEventProps.links = [
		{
			type: 'secondary',
			text: 'View',
			link: proposalLink.link,
		},
	];
	if ( updatedEventProps.event.proposal?.eventDate ) {
		const viewingUserId = user?.isLoggedIn ? user.user.id : undefined;
		const proposalContact = updatedEventProps.event.proposal.contact;
		const { viewingUserIsAdminOrManager, viewingUserIsAssignedMember } =
			proposalContact && viewingUserId
				? rpcShared.contacts.getViewingUserDocumentRelationship( {
					contact: proposalContact,
					viewingUserId,
				  } )
				: {
					viewingUserIsAdminOrManager: false,
					viewingUserIsAssignedMember: false,
				  };
		const canSendCalendarInvite =
			viewingUserIsAdminOrManager || viewingUserIsAssignedMember;
		updatedEventProps.calendarLinksProps = {
			sourceType: CalendarSourceType.PROPOSAL,
			sourceId: updatedEventProps.event.proposal.id,
			iconOnly: false,
			canSendCalendarInvite,
		};
	}

	if (
		defaultEventProps.event.category === EventCategory.ProposalAcceptedByPlanner
	) {
		if ( isVendorDetail ) {
			updatedEventProps.message = (
				<Typography>{ toSimpleLink( proposalLink ) } has been accepted.</Typography>
			);
		} else {
			updatedEventProps.message = (
				<Typography>
					{ customerLink } has accepted a proposal from { toSimpleLink( vendorLink ) }
				</Typography>
			);
		}
	} else if (
		defaultEventProps.event.category ===
		EventCategory.ProposalAcceptedByCustomer
	) {
		if ( assignedPlannerOrAdmin ) {
			updatedEventProps.message = (
				<Typography>
					{ customerLink } has accepted a proposal from { toSimpleLink( vendorLink ) }
				</Typography>
			);
		} else {
			updatedEventProps.message = (
				<Typography>{ toSimpleLink( proposalLink ) } has been accepted.</Typography>
			);
		}
	}

	return updatedEventProps;
};
